import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, DollarSign, Clock, Wifi, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auto-detect brand from hostname so each brand's /jobs page only
// serves the roles the admin flagged for that site. Preview URLs
// containing "careers" also count as the careers brand — matches the
// same rule used by the newsletter popup in App.js.
function _detectBrand() {
  if (typeof window === "undefined") return "collective";
  const host = window.location.hostname.toLowerCase();
  if (host.includes("theditchcareers") || host.includes("careers.")) return "careers";
  return "collective";
}
const BRAND = _detectBrand();

/**
 * Public /jobs page — live roles from `careers_roles` served via
 * /api/public/roles. Hiring companies stay anonymous by design: every
 * application routes through The Ditch Careers candidate intake so
 * candidates cannot go direct and skip our recruitment pipeline.
 */
export default function Jobs() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/roles?limit=120&brand=${BRAND}`);
        setRoles(r.data?.roles || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return roles;
    return roles.filter((r) =>
      [r.title, r.industry, r.location, r.seniority, r.employment_type, ...(r.tags || [])]
        .filter(Boolean).join(" ").toLowerCase().includes(needle)
    );
  }, [q, roles]);

  return (
    <div className="min-h-screen bg-[#F8F9FB] text-[#0D2F4D]" data-testid="jobs-page">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="bg-[#0D2F4D] text-white py-16 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C6D21F] font-bold">The Ditch Careers</p>
          <h1 className="font-display text-4xl md:text-6xl font-extrabold mt-3 leading-tight">
            Live Australian roles<br />for Kiwis making the move.
          </h1>
          <p className="text-lg text-white/80 mt-5 max-w-2xl">
            Every role below is live and worked by our recruitment team. We introduce you to the
            employer directly — you don't chase job boards alone.
          </p>

          <div className="mt-8 max-w-xl flex items-center gap-2 bg-white rounded-full pl-5 pr-2 py-2">
            <Search size={18} className="text-[#6F8FA8]" />
            <Input
              data-testid="jobs-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search role, industry, city…"
              className="border-0 shadow-none focus-visible:ring-0 text-[#0D2F4D] placeholder:text-[#8AA0B6]"
            />
          </div>
        </div>
      </section>

      {/* ── Grid ─────────────────────────────────────────────────────── */}
      <section className="max-w-[1200px] mx-auto px-6 py-14">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-display text-2xl font-extrabold">
            {loading ? "Loading roles…" : `${filtered.length} live ${filtered.length === 1 ? "role" : "roles"}`}
          </h2>
          <Link to="/roles" className="text-sm text-[#0D2F4D] underline" data-testid="jobs-register-link">
            Register as a candidate →
          </Link>
        </div>

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E4E7EA] p-12 text-center"
               data-testid="jobs-empty">
            <Briefcase size={32} className="mx-auto text-[#6F8FA8]" />
            <p className="mt-4 text-[#0D2F4D] font-semibold">No roles match that search.</p>
            <p className="text-sm text-[#6F8FA8] mt-1">
              New roles land weekly — <Link to="/roles" className="underline">register as a candidate</Link>
              {" "}and we'll match you to what's coming next.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5" data-testid="jobs-list">
          {filtered.map((r) => (
            <article
              key={r.id}
              data-testid={`job-card-${r.id}`}
              className="bg-white rounded-2xl border border-[#E4E7EA] p-6 flex flex-col hover:border-[#0D2F4D] transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-xl font-extrabold leading-snug">{r.title}</h3>
                  {r.industry && (
                    <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold mt-1">
                      {r.industry}
                    </p>
                  )}
                </div>
                <Badge className="bg-[#C6D21F] text-[#0D2F4D] rounded-full text-[10px] uppercase font-bold shrink-0">
                  Live
                </Badge>
              </div>

              <div className="flex flex-wrap gap-4 mt-4 text-sm text-[#0D2F4D]">
                {r.location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {r.location}</span>}
                {r.employment_type && <span className="flex items-center gap-1.5"><Clock size={14} /> {r.employment_type}</span>}
                {r.seniority && <span className="flex items-center gap-1.5"><Briefcase size={14} /> {r.seniority}</span>}
                {r.remote_ok && <span className="flex items-center gap-1.5 text-[#0D2F4D] font-semibold"><Wifi size={14} /> Remote OK</span>}
              </div>

              {r.salary_range && (
                <p className="mt-3 text-sm font-semibold text-[#0D2F4D] flex items-center gap-1.5">
                  <DollarSign size={14} /> {r.salary_range}
                </p>
              )}

              {r.description && (
                <p className="mt-4 text-sm text-[#3E5B75] leading-relaxed line-clamp-4">
                  {r.description}
                </p>
              )}

              {(r.requirements || []).length > 0 && (
                <ul className="mt-4 space-y-1.5 text-sm text-[#0D2F4D]">
                  {r.requirements.slice(0, 3).map((req) => (
                    <li key={req} className="flex gap-2">
                      <span className="text-[#C6D21F] font-bold">·</span>
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Employer stays anonymous — apply routes through us. */}
              <p className="mt-5 text-xs text-[#6F8FA8] italic">
                Hiring company disclosed after we confirm you're a fit.
              </p>

              <div className="mt-4 pt-4 border-t border-[#E4E7EA] flex items-center justify-between">
                <span className="text-xs text-[#6F8FA8]">
                  Represented by The Ditch Careers
                </span>
                <Link to={`/roles?role=${r.id}`}>
                  <Button
                    size="sm"
                    data-testid={`job-apply-${r.id}`}
                    className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F] rounded-full"
                  >
                    Apply through us <ArrowRight size={14} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 bg-white rounded-2xl border border-[#E4E7EA] p-8 md:p-10 text-center max-w-3xl mx-auto">
          <h3 className="font-display text-2xl font-extrabold">Can't see the right fit yet?</h3>
          <p className="text-sm text-[#3E5B75] mt-3">
            New roles land every week. Register as a candidate and we'll match you to what's coming
            next — plus present your CV to suitable employers directly.
          </p>
          <Link to="/roles" className="inline-block mt-6">
            <Button className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F] rounded-full px-6"
                    data-testid="jobs-cta-register">
              Register as a candidate <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
